import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { defaultEQM4010_R01HeaderDto, EQM4010_R01HeaderDto } from './Dto_EQM4010_R01';
import { useEQM4010_R01Store } from './Store_EQM4010_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'EQM4010_R01-mold-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<EQM4010_R01HeaderDto> | null>;
}

export const ItemTable = (props: Props) => {
	const { gridRef } = props;

	const {
		EQM4010_R01List,
		selectedEQM4010_R01,
		setSelectedEQM4010_R01,
		initSelectedEQM4010_R01,
		columnState,
		setColumnState,
		scrollTop, setScrollTop,
		setDetailScrollTop,
	} = useEQM4010_R01Store();

	const { moldTypeList } = useCommonCodeStore();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'moldCd',
		gridData: EQM4010_R01List,
		defaultGridData: defaultEQM4010_R01HeaderDto,
		selectedRow: selectedEQM4010_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		moldCd: '금형코드',
		moldNm: '금형명',
		moldType: '금형유형',
		currentShot: '현재타수',
		shotLimit: '한계타수',
		itemCd: '생산품목',
		itemNm: '품명',
		cavity: 'CAVITY',
		remark: '비고',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'moldCd')(params), []);

	const baseColumnDefs: ColDef<EQM4010_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '금형코드', colId: 'moldCd', field: 'moldCd', width: 110,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'moldCd'),
		},
		{
			headerName: '금형명', colId: 'moldNm', field: 'moldNm', width: 160,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'moldNm'),
		},
		{
			headerName: '금형유형', colId: 'moldType', field: 'moldType', width: 110,
			headerClass: aggridHeaderClass,
			valueFormatter: (params: { data?: EQM4010_R01HeaderDto }) => {
				const code = params.data?.moldType;
				if (!code) return '';
				return moldTypeList.find((value) => value.minorCd === code)?.codeNm || '';
			},
			cellStyle: (p) => getCellStyle(p, 'moldType'),
		},
		{
			headerName: '현재타수', colId: 'currentShot', field: 'currentShot', width: 100,
			headerClass: aggridHeaderClass,
			type: 'numericColumn',
			valueFormatter: (params: { data?: EQM4010_R01HeaderDto }) =>
				params.data?.currentShot != null ? Number(params.data.currentShot).toLocaleString() : '',
			cellStyle: (p) => getCellStyle(p, 'currentShot'),
		},
		{
			headerName: '한계타수', colId: 'shotLimit', field: 'shotLimit', width: 110,
			headerClass: aggridHeaderClass,
			type: 'numericColumn',
			valueFormatter: (params: { data?: EQM4010_R01HeaderDto }) =>
				params.data?.shotLimit != null ? Number(params.data.shotLimit).toLocaleString() : '',
			cellStyle: (p) => getCellStyle(p, 'shotLimit'),
		},
		{
			headerName: '생산품목', colId: 'itemCd', field: 'itemCd', width: 130,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemCd'),
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 160,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemNm'),
		},
		{
			headerName: 'CAVITY', colId: 'cavity', field: 'cavity', width: 90,
			headerClass: aggridHeaderClass,
			cellRenderer: (p: { data?: EQM4010_R01HeaderDto }) => p.data?.cavity?.toLocaleString(),
			cellStyle: (p) => getCellStyle(p, 'cavity'),
		},
		{
			headerName: '비고', colId: 'remark', field: 'remark', width: 180,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'remark'),
		},
	], [getCellStyle, moldTypeList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<EQM4010_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<EQM4010_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<EQM4010_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData?.moldCd) return;

		const isSelected = selectedEQM4010_R01?.moldCd === rowData.moldCd;
		setSelectedEQM4010_R01(isSelected ? undefined : rowData);
		setDetailScrollTop(null);
	}, [selectedEQM4010_R01, setSelectedEQM4010_R01, setDetailScrollTop]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		moldCd: `${EQM4010_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [EQM4010_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [EQM4010_R01List, selectedEQM4010_R01]);

	return (
		<GridContextWrapper<EQM4010_R01HeaderDto>
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			enableCellCalculation
			initSelected={initSelectedEQM4010_R01}
			defaultRowData={defaultEQM4010_R01HeaderDto}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<EQM4010_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<EQM4010_R01HeaderDto>({
					idKey: 'moldCd',
					selectedList: selectedEQM4010_R01 ? [selectedEQM4010_R01.moldCd] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
